import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./lib/utils";

const textareaVariants = cva(
  "flex w-full rounded-md border border-input bg-background text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
  {
    variants: {
      /**
       * A step on the @smallbiz/design-tokens density scale (DensityMode). `comfortable`
       * reproduces the original fixed sizing (min-h-[80px] px-3 py-2) — every existing
       * consumer is untouched. `compact` (opt-in, #1134) is the dense, keyboard-first step:
       * tighter padding + native CSS `field-sizing-content` auto-grow (zero-JS, no focus-jump)
       * from a ~2-row minimum. The values are hardcoded utilities for now; #1141 folds this
       * into the token-driven, rescaled density scale (and adds `spacious`).
       */
      density: {
        comfortable: "min-h-[80px] px-3 py-2",
        compact: "min-h-10 px-2 py-1 field-sizing-content",
      },
      resize: {
        none: "resize-none",
        vertical: "resize-y",
        horizontal: "resize-x",
        both: "resize",
      },
    },
    defaultVariants: {
      density: "comfortable",
      resize: "vertical",
    },
  },
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, resize, density, ...props }, ref) => {
    return (
      <textarea
        className={cn(textareaVariants({ resize, density }), className)}
        ref={ref}
        {...props}
      />
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
